import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileUp, Layers, Landmark, CircleCheck, FileText, Search, Printer, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { OpFila } from "./OpFila";
import { DepositoDialog } from "./DepositoDialog";
import { Pill, toB64 } from "./util";

const PASOS = [[FileText, "Subir finiquito", "(PDF o fotos de Cargo Expreso)"], [Layers, "Se identifican", "las guías y pedidos"], [Landmark, "Registra el depósito", "(banco, número y fecha)"], [CircleCheck, "Confirma el pago", "y factura el pedido"]];
const POR_PAG = 10;

export const Liquidaciones = () => {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [pag, setPag] = useState(1);
  const [empresa, setEmpresa] = useState("Cargo Expreso");
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState({});
  const [dlg, setDlg] = useState(false);
  const ref = useRef(null);
  const seq = useRef(0);
  const load = useCallback(() => {
    const n = ++seq.current;
    api.get("/mi-dinero/liquidaciones", { params: { q, desde, hasta } }).then(({ data }) => { if (n === seq.current) setD(data); }).catch((e) => toast.error(formatApiError(e)));
  }, [q, desde, hasta]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setPag(1); }, [q, desde, hasta]);
  const subir = async (files) => {
    setBusy(true);
    for (const f of files) {
      try {
        const { data } = await api.post("/mi-dinero/finiquitos", { filename: f.name, empresa, mime: f.type || "application/pdf", pdf_base64: await toB64(f) });
        if (data.duplicado_archivo) toast.info(`${f.name}: ya se había subido. No se duplicó nada.`);
        else toast.success(`${f.name}: ${data.guias} guías · ${data.nuevas} nuevas · ${data.actualizadas || 0} actualizadas · ${data.duplicadas} ya existían${data.bloqueadas ? ` · ${data.bloqueadas} sin cambiar (ya confirmadas)` : ""} · ${data.sin_pedido} sin pedido`, { duration: 8000 });
      } catch (e) { toast.error(`${f.name}: ${formatApiError(e)}`); }
    }
    setBusy(false); if (ref.current) ref.current.value = ""; load();
  };
  const ops = d?.operaciones || [];
  const pags = Math.max(1, Math.ceil(ops.length / POR_PAG));
  const vis = ops.slice((pag - 1) * POR_PAG, pag * POR_PAG);
  const elegidas = ops.filter((o) => sel[`${o.empresa}-${o.operacion}`] && !o.deposito_id);
  return (
    <div className="space-y-3" data-testid="liquidaciones">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {PASOS.map(([I, a, b], i) => (
          <div key={a} className="rounded-xl border bg-white px-3 py-2 flex items-center gap-2">
            <I className="w-6 h-6 text-violet-700" /><span className="w-6 h-6 rounded-full bg-violet-700 text-white text-xs font-black flex items-center justify-center">{i + 1}</span>
            <div className="text-xs leading-tight"><b>{a}</b><br />{b}</div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select value={empresa} onChange={(e) => setEmpresa(e.target.value)} className="border rounded-lg px-2 py-2 text-sm" data-testid="liq-empresa">
          <option>Cargo Expreso</option><option>Otra empresa</option>
        </select>
        <Button disabled={busy} onClick={() => ref.current?.click()} className="bg-violet-700 hover:bg-violet-800" data-testid="liq-subir"><FileUp className="w-4 h-4 mr-1" />{busy ? "Leyendo…" : "Subir finiquito (PDF o fotos)"}</Button>
        <input ref={ref} type="file" multiple accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => e.target.files?.length && subir([...e.target.files])} data-testid="liq-file" />
        <Pill tone="zinc" testid="liq-n-finiquitos">Finiquitos cargados ({d?.finiquitos ?? 0})</Pill>
        <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm" data-testid="liq-desde" />
        <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm" data-testid="liq-hasta" />
        <Pill tone="purple" testid="liq-n-ops">Operaciones: {d?.total ?? 0}</Pill>
        <Pill tone="amber" testid="liq-n-pend">Pendientes: {d?.pendientes ?? 0}</Pill>
        <Pill tone="green" testid="liq-n-conf">Confirmadas: {d?.confirmadas ?? 0}</Pill>
        <Button disabled={!elegidas.length} onClick={() => setDlg(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="liq-registrar-deposito">Registrar depósito ({elegidas.length})</Button>
      </div>
      <div className="relative">
        <Search className="absolute left-3 top-2.5 w-4 h-4 text-zinc-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por cliente, pedido, guía u operación…" className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm" data-testid="liq-buscar" />
      </div>
      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full text-sm">
          <thead className="bg-violet-700 text-white text-[11px]">
            <tr>{["Cliente / Destinatario", "Pedido", "Guía", "Operación", "Monto COD", "Comisión", "Neto por guía", "Total a recibir en el banco (por operación)", "Banco", "N° de depósito / Transacción", "Fecha depósito", "Estado", "Subir comprobante", "Facturación"].map((h, i) => (
              <th key={h} className={`px-1.5 py-2 text-left ${i === 7 ? "bg-amber-200 text-zinc-900 text-center" : ""}`}>{h}</th>
            ))}</tr>
          </thead>
          {vis.map((op) => { const k = `${op.empresa}-${op.operacion}`; return <OpFila key={k} op={op} onChanged={load} sel={sel[k]} onSel={(v) => setSel({ ...sel, [k]: v })} />; })}
        </table>
        {d && !ops.length && <div className="p-6 text-center text-sm text-zinc-500" data-testid="liq-vacio">Suba un finiquito PDF para comenzar.</div>}
      </div>
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <span className="text-sm mr-auto">Mostrando {ops.length ? (pag - 1) * POR_PAG + 1 : 0} – {Math.min(pag * POR_PAG, ops.length)} de {ops.length} operaciones</span>
        <Button variant="outline" size="sm" disabled={pag <= 1} onClick={() => setPag(pag - 1)} data-testid="liq-prev"><ChevronLeft className="w-4 h-4" /></Button>
        <span className="text-sm font-bold" data-testid="liq-pagina">{pag} / {pags}</span>
        <Button variant="outline" size="sm" disabled={pag >= pags} onClick={() => setPag(pag + 1)} data-testid="liq-next"><ChevronRight className="w-4 h-4" /></Button>
        <Button variant="outline" onClick={() => window.print()} data-testid="liq-imprimir"><Printer className="w-4 h-4 mr-1" />Imprimir</Button>
      </div>
      {dlg && <DepositoDialog ops={elegidas} onClose={() => setDlg(false)} onSaved={() => { setDlg(false); setSel({}); load(); }} />}
    </div>
  );
};
